/**
 * Situation recommendation engine — applies the authored situation rules to a
 * default (shape-matrix) recommendation, within the set of tools the readiness
 * evaluator has made available.
 *
 * Pure. No DB access, no React. Precedence, in one line:
 *
 *   readiness / compatibility (hard)  >  situation rules (advisory)  >  shape matrix (default)
 *
 * Guarantees (each asserted by situationRecommendation.test.ts against the full
 * method set, not by inspection):
 *
 *   1. `orderedToolIds` never contains a tool outside `availableToolIds`.
 *      A rule cannot promote a blocked tool; the attempt is recorded in
 *      `suppressed` so the UI can say so if it wants to.
 *   2. With no vector (or a vector that fires no rule) the output is the
 *      default order restricted to the available set, with no annotations and
 *      `applied === false` — callers can keep today's rendering untouched.
 *   3. A discouragement only annotates. It never removes a tool.
 *   4. When a decision class is supplied, a promotion is suppressed for any
 *      tool whose every method-registry variant is `not_fit` for that class.
 *
 * Source: docs/9-exploration/PRD-method-recommendation.md §5 Phase 3.
 */

import type { DecisionClass } from '../types/prioritization/session';
import type { SituationVector } from '../types/prioritization/situationVector';
import { isEmptySituationVector } from '../types/prioritization/situationVector';
import { getVariantsForTool } from './methodRegistry';
import { SITUATION_RULES, ruleMatches, ruleSpecificity, type SituationRule } from './situationRules';

export interface SituationRecommendationInput {
  vector: SituationVector | null | undefined;
  /**
   * Tool ids the readiness evaluator reports as available (compatible and
   * prerequisites met), in the shape-matrix id space. Rules only reorder
   * within this set.
   */
  availableToolIds: readonly string[];
  /** The default order — the shape matrix's `primaryToolIds`. May include unavailable ids. */
  defaultToolIds: readonly string[];
  /** When known, promotions are additionally restricted to tools that fit this class. */
  decisionClass?: DecisionClass | null;
  /** Injectable for tests. Defaults to the authored table. */
  rules?: readonly SituationRule[];
}

export interface SituationToolAnnotation {
  toolId: string;
  kind: 'promoted' | 'discouraged';
  ruleId: string;
  /** Promoted: the rule's rationale. Discouraged: the per-tool reason. Shown verbatim. */
  text: string;
}

export interface SuppressedPromotion {
  toolId: string;
  ruleId: string;
  reason: 'blocked' | 'not_fit';
}

export interface SituationEscape {
  toDecisionClass: DecisionClass;
  because: string;
  ruleId: string;
}

export interface SituationRecommendation {
  /**
   * Available tools only: promoted tools first (rule order, most specific rule
   * first), then the default order, then whatever else is available.
   */
  orderedToolIds: string[];
  annotations: SituationToolAnnotation[];
  /** Rules that matched the vector, most specific first. */
  firedRules: SituationRule[];
  /** Set when a fired rule says this situation calls for a different session first. */
  escape: SituationEscape | null;
  /** Promotions that could not apply, and why. */
  suppressed: SuppressedPromotion[];
  /** True when a vector was supplied and at least one rule fired. */
  applied: boolean;
}

/** A tool fits a decision class when at least one of its registry variants is not `not_fit`. Unknown tools fit (the registry is not the gate; readiness is). */
export function toolFitsDecisionClass(toolId: string, decisionClass: DecisionClass | null | undefined): boolean {
  if (!decisionClass) return true;
  const variants = getVariantsForTool(toolId);
  if (variants.length === 0) return true;
  return variants.some((v) => v.sessionFit[decisionClass] !== 'not_fit');
}

/** Rules matching the vector, most specific first; ties keep table order. */
export function matchingRules(
  vector: SituationVector | null | undefined,
  rules: readonly SituationRule[] = SITUATION_RULES,
): SituationRule[] {
  if (isEmptySituationVector(vector)) return [];
  return rules
    .map((rule, index) => ({ rule, index }))
    .filter(({ rule }) => ruleMatches(rule, vector))
    .sort((a, b) => ruleSpecificity(b.rule) - ruleSpecificity(a.rule) || a.index - b.index)
    .map(({ rule }) => rule);
}

function uniqueInOrder(ids: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

export function evaluateSituationRules(input: SituationRecommendationInput): SituationRecommendation {
  const available = new Set(input.availableToolIds);
  const defaultOrder = uniqueInOrder(input.defaultToolIds).filter((id) => available.has(id));
  const restOfAvailable = uniqueInOrder(input.availableToolIds).filter((id) => !defaultOrder.includes(id));
  const fired = matchingRules(input.vector, input.rules ?? SITUATION_RULES);

  if (fired.length === 0) {
    return {
      orderedToolIds: [...defaultOrder, ...restOfAvailable],
      annotations: [],
      firedRules: [],
      escape: null,
      suppressed: [],
      applied: false,
    };
  }

  const promoted: string[] = [];
  const annotations: SituationToolAnnotation[] = [];
  const suppressed: SuppressedPromotion[] = [];
  let escape: SituationEscape | null = null;

  for (const rule of fired) {
    for (const toolId of rule.prefer) {
      if (!available.has(toolId)) {
        suppressed.push({ toolId, ruleId: rule.id, reason: 'blocked' });
        continue;
      }
      if (!toolFitsDecisionClass(toolId, input.decisionClass)) {
        suppressed.push({ toolId, ruleId: rule.id, reason: 'not_fit' });
        continue;
      }
      if (!promoted.includes(toolId)) promoted.push(toolId);
      annotations.push({ toolId, kind: 'promoted', ruleId: rule.id, text: rule.rationale });
    }
    for (const { toolId, because } of rule.discourage) {
      if (!available.has(toolId)) continue; // nothing to annotate — it is not on offer
      annotations.push({ toolId, kind: 'discouraged', ruleId: rule.id, text: because });
    }
    if (rule.escalate && !escape) {
      escape = { ...rule.escalate, ruleId: rule.id };
    }
  }

  const orderedToolIds = uniqueInOrder([...promoted, ...defaultOrder, ...restOfAvailable]);

  return { orderedToolIds, annotations, firedRules: fired, escape, suppressed, applied: true };
}

/** Annotations for one tool, promoted first. */
export function annotationsForTool(
  recommendation: SituationRecommendation,
  toolId: string,
): SituationToolAnnotation[] {
  return recommendation.annotations
    .filter((a) => a.toolId === toolId)
    .sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'promoted' ? -1 : 1));
}
